/**
 * COSFY API Client
 * 
 * Handles authentication, token refresh, and all backend communication.
 * Falls back gracefully when backend is unreachable (demo mode).
 */

const BASE = "/api/v1";

// ── Token Storage ──────────────────────────────────────────────

let accessToken = localStorage.getItem("cosfy_access") || null;
let refreshToken = localStorage.getItem("cosfy_refresh") || null;

export function setTokens(access, refresh) {
  accessToken = access;
  refreshToken = refresh;
  if (access) localStorage.setItem("cosfy_access", access);
  else localStorage.removeItem("cosfy_access");
  if (refresh) localStorage.setItem("cosfy_refresh", refresh);
  else localStorage.removeItem("cosfy_refresh");
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem("cosfy_access");
  localStorage.removeItem("cosfy_refresh");
}

export function hasToken() {
  return !!accessToken;
}

// ── Core Fetch Wrapper ─────────────────────────────────────────

async function tryRefresh() {
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    setTokens(data.access_token, data.refresh_token || refreshToken);
    return true;
  } catch {
    return false;
  }
}

async function apiFetch(path, opts = {}) {
  const url = `${BASE}${path}`;
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  let res;
  try {
    res = await fetch(url, { ...opts, headers });
  } catch (err) {
    throw { offline: true, message: "Backend unreachable" };
  }

  // Token expired — try refresh once
  if (res.status === 401 && refreshToken) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      headers["Authorization"] = `Bearer ${accessToken}`;
      res = await fetch(url, { ...opts, headers });
    }
  }

  if (!res.ok) {
    let detail;
    try { detail = await res.json(); } catch { detail = { detail: res.statusText }; }
    throw { status: res.status, ...detail };
  }

  return res.json();
}

// ── Auth ───────────────────────────────────────────────────────

export async function login(email, password) {
  const data = await apiFetch("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  setTokens(data.access_token, data.refresh_token);
  return data;
}

export async function register(email, password, name, orgId) {
  return apiFetch("/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password, name, org_id: orgId }),
  });
}

export async function getMe() {
  return apiFetch("/auth/me");
}

export async function logout() {
  try { await apiFetch("/auth/logout", { method: "POST" }); } catch {}
  clearTokens();
}

export async function getSSOUrl(provider) {
  return apiFetch(`/auth/sso/${provider}/url`);
}

// ── Search ─────────────────────────────────────────────────────

export async function search(query, opts = {}) {
  return apiFetch("/search/", {
    method: "POST",
    body: JSON.stringify({
      query,
      org_id: opts.orgId || "",
      user_email: opts.userEmail || "",
      sources: opts.sources || [],
      date_from: opts.dateFrom || null,
      date_to: opts.dateTo || null,
      limit: opts.limit || 20,
      offset: opts.offset || 0,
      include_facets: true,
    }),
  });
}

export async function searchSuggest(query, orgId) {
  return apiFetch(`/search/suggest?q=${encodeURIComponent(query)}&org_id=${orgId}`);
}

// ── Chat ───────────────────────────────────────────────────────

export async function createConversation() {
  return apiFetch("/chat/conversations", { method: "POST", body: JSON.stringify({}) });
}

export async function listConversations(limit = 20) {
  return apiFetch(`/chat/conversations?limit=${limit}`);
}

export async function getConversation(id) {
  return apiFetch(`/chat/conversations/${id}`);
}

export async function deleteConversation(id) {
  return apiFetch(`/chat/conversations/${id}`, { method: "DELETE" });
}

export async function sendMessage(conversationId, content, sources = []) {
  return apiFetch(`/chat/conversations/${conversationId}/messages`, {
    method: "POST",
    body: JSON.stringify({ content, sources }),
  });
}

export function streamMessage(conversationId, content) {
  const url = `${BASE}/chat/conversations/${conversationId}/messages/stream`;
  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({ content }),
  });
}

export async function quickAsk(query) {
  return apiFetch("/chat/ask", {
    method: "POST",
    body: JSON.stringify({ query }),
  });
}

// ── Connectors ─────────────────────────────────────────────────

export async function listConnectors() {
  const token = accessToken || "";
  let orgId = "";
  try { orgId = JSON.parse(atob(token.split(".")[1])).org; } catch(e) {}
  return apiFetch(`/connectors/?org_id=${orgId}`);
}

export async function listAvailableConnectors() {
  return apiFetch("/connectors/available");
}

export async function getConnectorStatus(id) {
  return apiFetch(`/connectors/${id}/status`);
}

export async function triggerSync(id, jobType = "incremental") {
  return apiFetch(`/connectors/${id}/sync`, {
    method: "POST",
    body: JSON.stringify({ job_type: jobType }),
  });
}

export async function disconnectConnector(id) {
  return apiFetch(`/connectors/${id}`, { method: "DELETE" });
}

// ── Go Links ───────────────────────────────────────────────────

export async function listGoLinks() {
  return apiFetch("/go-links/");
}

export async function createGoLink(slug, destinationUrl, description) {
  return apiFetch("/go-links/", {
    method: "POST",
    body: JSON.stringify({ slug, destination_url: destinationUrl, description }),
  });
}

export async function popularGoLinks() {
  return apiFetch("/go-links/popular");
}

// ── Collections ────────────────────────────────────────────────

export async function listCollections() {
  return apiFetch("/collections/");
}

export async function getCollection(name) {
  return apiFetch(`/collections/${encodeURIComponent(name)}`);
}

export async function createCollection(name, description) {
  return apiFetch(`/collections/${encodeURIComponent(name)}`, {
    method: "POST",
    body: JSON.stringify({ description }),
  });
}

// ── People ─────────────────────────────────────────────────────

export async function searchPeople(query, orgId, limit = 20) {
  const params = new URLSearchParams({ q: query, org_id: orgId, limit: String(limit) });
  return apiFetch(`/people/?${params}`);
}

export async function getPerson(userId) {
  return apiFetch(`/people/${userId}`);
}

export async function listDepartments(orgId) {
  return apiFetch(`/people/departments/list?org_id=${orgId}`);
}

// ── Notifications ──────────────────────────────────────────────

export async function listNotifications(limit = 20) {
  return apiFetch(`/notifications/?limit=${limit}`);
}

export async function markNotificationRead(id) {
  return apiFetch(`/notifications/${id}/read`, { method: "POST" });
}

export async function markAllNotificationsRead() {
  return apiFetch("/notifications/read-all", { method: "POST" });
}

// ── Agents ─────────────────────────────────────────────────────

export async function listAgents() {
  return apiFetch("/agents/");
}

export async function getAgent(id) {
  return apiFetch(`/agents/${id}`);
}

export async function runAgent(id, input = {}) {
  return apiFetch(`/agents/${id}/run`, {
    method: "POST",
    body: JSON.stringify({ input }),
  });
}

export async function listAgentTemplates() {
  return apiFetch("/agents/templates/catalog");
}

export async function getAgentRuns(id) {
  return apiFetch(`/agents/${id}/runs`);
}

// ── Announcements ──────────────────────────────────────────────

export async function listAnnouncements() {
  return apiFetch("/announcements/");
}

export async function createAnnouncement(data) {
  return apiFetch("/announcements/", { method: "POST", body: JSON.stringify(data) });
}

export async function acknowledgeAnnouncement(id) {
  return apiFetch(`/announcements/${id}/acknowledge`, { method: "POST" });
}

// ── Admin ──────────────────────────────────────────────────────

export async function getAdminDashboard() {
  return apiFetch("/admin/dashboard");
}

export async function getAdminAnalytics(period = "30d") {
  return apiFetch(`/admin/analytics?period=${period}`);
}

export async function getConnectorHealth() {
  return apiFetch("/admin/connectors/health");
}

// ── Answers ────────────────────────────────────────────────────

export async function listVerifiedAnswers() {
  return apiFetch("/answers/verified");
}

export async function matchAnswer(query, orgId) {
  return apiFetch(`/answers/match?q=${encodeURIComponent(query)}&org_id=${orgId}`);
}

// ── Activity ───────────────────────────────────────────────────

export async function listActivity(limit = 20) {
  return apiFetch(`/activity/?limit=${limit}`);
}

// ── Health ─────────────────────────────────────────────────────

export async function healthCheck() {
  return apiFetch("/health");
}

// ── Billing ───────────────────────────────────────────────────

export async function listPlans() {
  return apiFetch("/billing/plans");
}

export async function getSubscription() {
  return apiFetch("/billing/subscription");
}

export async function createSubscription(planSlug, quantity = 1, email = "") {
  return apiFetch("/billing/subscribe", {
    method: "POST",
    body: JSON.stringify({ plan_slug: planSlug, quantity, customer_email: email || undefined }),
  });
}

export async function verifyPayment(paymentId, subscriptionId, signature) {
  return apiFetch("/billing/verify", {
    method: "POST",
    body: JSON.stringify({
      razorpay_payment_id: paymentId,
      razorpay_subscription_id: subscriptionId,
      razorpay_signature: signature,
    }),
  });
}

export async function cancelSubscription(atPeriodEnd = true) {
  return apiFetch("/billing/cancel", {
    method: "POST",
    body: JSON.stringify({ at_period_end: atPeriodEnd }),
  });
}

export async function getBillingUsage() {
  return apiFetch("/billing/usage");
}

export async function listInvoices(limit = 20) {
  return apiFetch(`/billing/invoices?limit=${limit}`);
}

export async function syncPlans() {
  return apiFetch("/billing/admin/sync-plans", { method: "POST" });
}

// ── Razorpay Checkout Helper ──────────────────────────────────

export function openRazorpayCheckout(subscriptionId, keyId, onSuccess, onFailure) {
  const options = {
    key: keyId,
    subscription_id: subscriptionId,
    name: "Cosfy",
    description: "Enterprise AI Search Platform",
    theme: { color: "#4F5BD5" },
    handler: async (response) => {
      try {
        await verifyPayment(
          response.razorpay_payment_id,
          response.razorpay_subscription_id,
          response.razorpay_signature,
        );
        if (onSuccess) onSuccess(response);
      } catch (err) {
        if (onFailure) onFailure(err);
      }
    },
    modal: {
      ondismiss: () => { if (onFailure) onFailure({ dismissed: true }); },
    },
  };

  if (typeof window.Razorpay === "undefined") {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => new window.Razorpay(options).open();
    document.head.appendChild(script);
  } else {
    new window.Razorpay(options).open();
  }
}

// ── CA / Jaanch AI endpoints ────────────────────────────────────

export async function runGSTReconciliation(clientName, gstin, period) {
  return apiFetch("/ca/reconcile/gst", {
    method: "POST",
    body: JSON.stringify({ client_name: clientName, gstin, period }),
  });
}

export async function getGSTReconHistory(gstin, limit = 20) {
  const q = gstin ? `?gstin=${gstin}&limit=${limit}` : `?limit=${limit}`;
  return apiFetch(`/ca/reconcile/gst/history${q}`);
}

export async function getComplianceCalendar(ay = null, refresh = false) {
  const params = new URLSearchParams();
  if (ay) params.set("ay", ay);
  if (refresh) params.set("refresh", "true");
  return apiFetch(`/ca/compliance/calendar?${params}`);
}

export async function getClientHealth() {
  return apiFetch("/ca/compliance/health");
}

export async function getNotices(status = "all") {
  return apiFetch(`/ca/notices?status=${status}`);
}

export async function uploadNotice(file, clientName) {
  const form = new FormData();
  form.append("file", file);
  return apiFetch(`/ca/notices/upload?client_name=${encodeURIComponent(clientName)}`, {
    method: "POST",
    headers: {},  // Remove Content-Type so fetch sets multipart boundary
    body: form,
  });
}

export async function prepareITR(clientName, pan, ay, fy) {
  return apiFetch("/ca/itr/prepare", {
    method: "POST",
    body: JSON.stringify({ client_name: clientName, pan, ay, fy }),
  });
}

export async function generateDocument(clientName, pan, fy, ay, documentType) {
  // Backend returns a FileResponse (binary .docx) — use fetch directly, trigger browser download
  const token = localStorage.getItem("cosfy_access") || "";
  const resp = await fetch("/api/v1/ca/documents/generate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: "Bearer " + token } : {}),
    },
    body: JSON.stringify({ client_name: clientName, pan, fy, ay, document_type: documentType }),
  });
  if (!resp.ok) {
    const err = await resp.text();
    throw new Error(err || "Document generation failed");
  }
  const blob = await resp.blob();
  const cd = resp.headers.get("content-disposition") || "";
 const match = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const filename = match ? match[2] : "document.docx";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a);
  a.click(); setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
  return { downloaded: true, filename };
}

export async function listCAClients() {
  return apiFetch("/ca/clients");
}

export async function getClientSummary(gstin) {
  return apiFetch(`/ca/clients/${gstin}/summary`);
}

export async function seedCAAgents() {
  return apiFetch("/ca/agents/seed", { method: "POST" });
}

export async function getCAAgentStatus() {
  return apiFetch("/ca/agents/status");
}

// ── New CA API methods (Session 5+) ──────────────────────────

// export async function runGSTR9Reconciliation(clientName, gstin, fy) {
//   return apiFetch("/ca/reconcile/gstr9", {
//     method: "POST",
//     body: JSON.stringify({ client_name: clientName, gstin, fy }),
//   });
// }

export async function sendWhatsAppReminder(gstin, phoneNumber, message, clientName = null) {
  return apiFetch(`/ca/clients/${gstin}/whatsapp-reminder`, {
    method: "POST",
    body: JSON.stringify({
      phone_number: phoneNumber,
      message,
      client_name: clientName,
    }),
  });
}

export async function sendBulkWhatsAppReminders() {
  return apiFetch("/ca/clients/bulk-whatsapp-reminder", { method: "POST" });
}

/**
 * Generate and download an engagement letter .docx file.
 * Returns a Blob (the raw .docx bytes).
 */
export async function generateEngagementLetter(params) {
  const {
    clientName, pan, gstin, fy, services,
    feesTotal, caFirm, caName, membershipNo,
  } = params;

  const token = localStorage.getItem("cosfy_access") || "";
  const res = await fetch("/api/v1/ca/documents/engagement-letter", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      client_name:   clientName,
      pan:           pan.toUpperCase(),
      gstin:         gstin || null,
      fy,
      services,
      fees_total:    feesTotal,
      ca_firm:       caFirm,
      ca_name:       caName,
      membership_no: membershipNo,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || "Engagement letter generation failed");
  }

  return res.blob();  // Caller creates object URL and triggers download
}

/**
 * Generate and download a Computation Sheet .docx file.
 */
export async function generateComputationSheet(params) {
  const token = localStorage.getItem("cosfy_access") || "";
  const res = await fetch("/api/v1/ca/documents/computation-sheet", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      client_name:    params.clientName,
      pan:            (params.pan || "").toUpperCase(),
      fy:             params.fy,
      ay:             params.ay || null,
      entity_type:    params.entityType || "company",
      gross_revenue:  params.grossRevenue || 0,
      other_income:   params.otherIncome || 0,
      total_expenses: params.totalExpenses || 0,
      depreciation:   params.depreciation || 0,
      disallowances:  params.disallowances || 0,
      deductions:     params.deductions || 0,
      tds_credit:     params.tdsCredit || 0,
      advance_tax:    params.advanceTax || 0,
      ca_firm:        params.caFirm || "",
      ca_name:        params.caName || "",
      membership_no:  params.membershipNo || "",
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || "Computation sheet generation failed");
  }
  return res.blob();
}

/**
 * Generate and download a Form 3CD skeleton .docx file.
 */
export async function generateForm3CD(params) {
  const token = localStorage.getItem("cosfy_access") || "";
  const res = await fetch("/api/v1/ca/documents/form3cd", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      client_name:         params.clientName,
      pan:                 (params.pan || "").toUpperCase(),
      fy:                  params.fy,
      ay:                  params.ay || null,
      entity_type:         params.entityType || "company",
      gross_revenue:       params.grossRevenue || 0,
      registered_address:  params.registeredAddress || "",
      principal_business:  params.principalBusiness || "",
      ca_firm:             params.caFirm || "",
      ca_name:             params.caName || "",
      membership_no:       params.membershipNo || "",
      frn:                 params.frn || "",
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || "Form 3CD generation failed");
  }
  return res.blob();
}

/**
 * Connect to the real-time compliance SSE stream.
 * Returns an EventSource — caller must call .close() on cleanup.
 *
 * Usage:
 *   const es = connectComplianceStream(
 *     (data) => console.log(data.overdue_count),
 *     (err)  => console.error(err)
 *   );
 *   // On component unmount:
 *   es.close();
 */
export function connectComplianceStream(onMessage, onError) {
  const token = localStorage.getItem("cosfy_access") || "";
  const url   = `/api/v1/ca/compliance/stream${token ? `?token=${token}` : ""}`;
  const es    = new EventSource(url);
  es.onmessage = (e) => {
    try { onMessage(JSON.parse(e.data)); } catch {}
  };
  if (onError) es.onerror = onError;
  return es;
}

/**
 * Trigger GSTR-9 Annual Reconciliation.
 */
export async function runGSTR9Reconciliation(clientName, gstin, fy) {
  return apiFetch("/ca/reconcile/gstr9", {
    method: "POST",
    body: JSON.stringify({ client_name: clientName, gstin, fy }),
  });
}

// ── WhatsApp Business API ─────────────────────────────────────

export async function setupWhatsApp(params) {
  return apiFetch("/ca/whatsapp/setup", {
    method: "POST",
    body: JSON.stringify({
      access_token:    params.accessToken,
      phone_number_id: params.phoneNumberId,
      business_id:     params.businessId || "",
      display_name:    params.displayName || "CA Firm",
    }),
  });
}

export async function testWhatsApp(testPhone) {
  return apiFetch("/ca/whatsapp/test", {
    method: "POST",
    body: JSON.stringify({ test_phone: testPhone }),
  });
}

export async function getWhatsAppStatus() {
  return apiFetch("/ca/whatsapp/status");
}

// ── Client Registry ───────────────────────────────────────────

export async function onboardClient(params) {
  return apiFetch("/ca/clients/onboard", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function getClientRegistry() {
  return apiFetch("/ca/clients/registry");
}

export async function removeClient(pan) {
  return apiFetch(`/ca/clients/${pan}`, { method: "DELETE" });
}

// ── Workspace / Multi-org ─────────────────────────────────────

export async function createWorkspace(params) {
  return apiFetch("/admin/workspaces", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function listWorkspaces(filters = {}) {
  const qs = new URLSearchParams(filters).toString();
  return apiFetch(`/admin/workspaces${qs ? "?" + qs : ""}`);
}

export async function switchWorkspaceVertical(workspaceId, vertical) {
  return apiFetch(`/admin/workspaces/${workspaceId}/vertical`, {
    method: "PATCH",
    body: JSON.stringify({ vertical, migrate_data: true }),
  });
}

export async function updateWorkspaceFeatures(workspaceId, features) {
  return apiFetch(`/admin/workspaces/${workspaceId}/features`, {
    method: "POST",
    body: JSON.stringify({ features }),
  });
}

export async function inviteUserToWorkspace(workspaceId, email, name, role = "user") {
  return apiFetch(`/admin/workspaces/${workspaceId}/users`, {
    method: "POST",
    body: JSON.stringify({ email, name, role, send_invite_email: true }),
  });
}
